
import type { HTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  // shrink-0 because the base sets `overflow-hidden`: without it a badge in a
  // flex row shrinks below its own text and hard-clips the label ("Accepted"
  // rendered as "Accepte"). Badges are short status text, so the row's other
  // children (titles, descriptions) are what should truncate. `max-w-full`
  // still caps the badge at the parent width, and text wraps rather than
  // clipping in that case. Call sites can opt back into shrinking by passing
  // `shrink` in className — twMerge lets it win.
  "inline-flex shrink-0 items-center rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring/50 focus:ring-offset-2 max-w-full overflow-hidden",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground",
        secondary:
          "border-transparent bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300",
        destructive:
          "border-transparent bg-red-500/90 text-white",
        outline: 
          "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 bg-transparent",
        custom: "border shadow-sm",
        success:
          "border-transparent bg-emerald-500/90 text-white",
        warning:
          "border-transparent bg-amber-500/90 text-white",
        info:
          "border-transparent bg-sky-500/90 text-white",
        purple:
          "border-transparent bg-violet-500/90 text-white",
        glass:
          "border border-slate-200/50 dark:border-slate-700/50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm text-slate-600 dark:text-slate-400",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface BadgeProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  )
}

export { Badge, badgeVariants }
